import { useEffect, useRef, useState } from 'react';

const MODAL_KEY = 'primumModal';

let nextModalId = 0;

function currentModal(): unknown {
  const state: unknown = window.history.state;

  return state && typeof state === 'object' && MODAL_KEY in state
    ? (state as Record<string, unknown>)[MODAL_KEY]
    : undefined;
}

/**
 * Lets the browser's back button close a full-screen modal on web, as it would a page.
 *
 * Opening the modal pushes a history entry for it on top of the page's own. It keeps the router's
 * entry id and the same address, so the router and the history ledger read it as the page they
 * are already on. Browser back then lands on the page's entry, and the modal closes.
 *
 * Closing the modal any other way (its Close button, Escape, the backdrop) takes its entry back
 * off, so the reader never needs a second back to leave the page. A modal that refuses to close
 * on back, such as one mid-save, gets its entry again, so the next back still reaches it.
 */
export function useCloseOnBrowserBack(isOpen: boolean, onClose: () => void) {
  const onCloseRef = useRef(onClose);

  onCloseRef.current = onClose;

  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    nextModalId += 1;

    const modalId = nextModalId;

    const pageState: unknown = window.history.state;

    window.history.pushState(
      { ...(pageState && typeof pageState === 'object' ? pageState : {}), [MODAL_KEY]: modalId },
      ''
    );

    let closedByBack = false;

    const onPopState = () => {
      if (currentModal() === modalId) {
        return;
      }

      closedByBack = true;

      onCloseRef.current();

      setGeneration((value) => value + 1);
    };

    window.addEventListener('popstate', onPopState);

    return () => {
      window.removeEventListener('popstate', onPopState);

      if (!closedByBack && currentModal() === modalId) {
        window.history.back();
      }
    };
  }, [isOpen, generation]);
}
